import React from 'react'
import { Link } from 'react-router-dom'
import { theme } from 'antd'
import type { GlobalToken } from 'antd'
import { fontSize, fontWeight, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { HeaderRule } from './HeaderRule'

export interface PageHeaderTrailItem {
  label: React.ReactNode
  /**
   * Router path. A `Link`, not an anchor: an anchor here reboots the whole app.
   *
   * Optional, because a nav *section* like "Leave setup" is a heading in the sidebar with no page
   * behind it. A level you cannot click is not a level you can be at, so it is left out of the trail
   * rather than drawn as inert text.
   */
  to?: string
  /**
   * The label as plain text, for the one place markup cannot go: the `title` of the chip that
   * stands in for collapsed levels. Needed because `label` is usually a translation element, and
   * an element rendered into a string attribute reads `[object Object]`. A string `label` is used
   * as-is; without either, the chip carries no tooltip rather than a broken one.
   */
  text?: string
}

/** Every word the trail renders, none of it visible. English defaults; the design system takes no
 * i18n dependency, so a real call site passes translations the way `DateNavigator` takes its. */
export interface PageHeaderTrailLabels {
  /** Accessible name for the nav landmark. The APG requires the region be labelled. */
  landmark: string
  /** Accessible name for the chip standing in for the levels it hides. */
  more: (count: number) => string
}

const DEFAULT_LABELS: PageHeaderTrailLabels = {
  landmark: 'Breadcrumb',
  more: (count) => `${count} more`,
}

export interface PageHeaderTrailProps {
  items: PageHeaderTrailItem[]
  /**
   * The page itself, as the last item - never a link, and in the body ink while the links stay
   * secondary, which is what distinguishes it.
   */
  current?: React.ReactNode
  labels?: Partial<PageHeaderTrailLabels>
}

const TRAIL_CLASS = 'vt-trail'
const LINK_CLASS = 'vt-trail-link'
const BACK_CLASS = 'vt-trail-back'
const OVERFLOW_CLASS = 'vt-trail-overflow'

/** 14px: the product's text floor. Tight tracking keeps it reading as a line under a title. */
const CRUMB_TYPE: React.CSSProperties = { fontSize: fontSize.base, letterSpacing: '-0.2px' }

/** Keep the first level and the last two; everything between collapses behind one chip. */
const VISIBLE_HEAD = 1
const VISIBLE_TAIL = 2

/**
 * The rules the trail cannot express inline: `:hover`, `:active` and `:focus-visible` have no inline
 * form, so without these a crumb never acknowledges the pointer and a keyboard user has no idea
 * where they are. Rendered by the component itself, the way `DataGrid` and `RecordCard` carry
 * theirs, rather than relying on a stylesheet every page has to load.
 *
 * The back control hovers exactly like the app's ghost buttons (`Button variant="text"`): antd's
 * text-button fill, ink unchanged. It is a ghost button in all but element, so it must not invent a
 * hover of its own - the brand tint it used to take made it the one purple thing on a line this
 * treatment exists to keep grey.
 */
function trailStyles(token: GlobalToken): string {
  return `
.${LINK_CLASS} { text-decoration: none; border-radius: ${token.borderRadiusSM}px; }
.${LINK_CLASS}:hover { text-decoration: underline; }

/* Resting AND hover colour both live here. They have to: an inline colour outranks any rule, so
   a resting colour set inline would make the hover unreachable. */
.${TRAIL_CLASS} .${LINK_CLASS} { color: ${token.colorTextSecondary}; }
.${TRAIL_CLASS} .${LINK_CLASS}:hover { color: ${token.colorText}; }

.${BACK_CLASS} { transition: background-color 120ms ease, color 120ms ease; }
.${BACK_CLASS}:hover { background: ${token.colorBgTextHover}; color: ${token.colorText}; }
.${BACK_CLASS}:active { background: ${token.colorBgTextActive}; }

.${OVERFLOW_CLASS} { transition: background-color 120ms ease, color 120ms ease; }
.${OVERFLOW_CLASS}:hover { background: ${token.colorBgTextHover}; color: ${token.colorText}; }

.${LINK_CLASS}:focus-visible, .${BACK_CLASS}:focus-visible {
  outline: 2px solid ${token.colorPrimaryBorder};
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .${BACK_CLASS}, .${OVERFLOW_CLASS} { transition: none; }
}
`
}

/** `margin: 0` is required: the app's stylesheets set `li { margin-left: 20px }`
 * for prose lists, and an ordered list is what the APG asks a breadcrumb to be - so without this
 * every crumb inherits a 20px indent from a rule written for something else entirely. */
const LIST_ITEM: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', margin: 0 }

/** Clipped, not `display: none` - a hidden name has to stay in the accessibility tree. */
const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
}

/**
 * Vacation Tracker's breadcrumb trail: the levels between the side nav and here, in greys, with a
 * ghost back control and a rule under them.
 */
export function PageHeaderTrail({ items: allItems, current, labels }: PageHeaderTrailProps) {
  const { token } = theme.useToken()
  const text = { ...DEFAULT_LABELS, ...labels }
  const items = allItems.filter((item) => Boolean(item.to))
  const parent = items[items.length - 1] as { label: React.ReactNode; to: string } | undefined

  const collapsed = items.length > VISIBLE_HEAD + VISIBLE_TAIL + 1
  const head = collapsed ? items.slice(0, VISIBLE_HEAD) : items
  const tail = collapsed ? items.slice(-VISIBLE_TAIL) : []
  const hidden = collapsed ? items.slice(VISIBLE_HEAD, -VISIBLE_TAIL) : []
  // Only when every hidden level can say its name in plain text - a tooltip listing half the path
  // is worse than none, and an element coerced into this attribute reads `[object Object]`.
  const hiddenNames = hidden.map((level) => level.text ?? (typeof level.label === 'string' ? level.label : undefined))
  const hiddenTitle = hiddenNames.every(Boolean) ? hiddenNames.join(' / ') : undefined

  const crumb = (item: PageHeaderTrailItem, index: number) => (
    <li key={item.to} style={LIST_ITEM}>
      {index > 0 && <SlashSeparator token={token} />}
      {/* No inline colour: an inline style outranks every stylesheet rule, so a hover colour set
          in CSS could never win. Resting and hover both come from the sheet. */}
      <Link to={item.to as string} className={LINK_CLASS} style={{ ...CRUMB_TYPE, fontWeight: fontWeight.regular }}>
        {item.label}
      </Link>
    </li>
  )

  const trail = (
    <nav aria-label={text.landmark}>
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          listStyle: 'none',
          margin: 0,
          padding: 0,
          color: token.colorTextSecondary,
        }}
      >
        {head.map(crumb)}
        {collapsed && (
          <li style={LIST_ITEM}>
            <SlashSeparator token={token} />
            <span
              className={OVERFLOW_CLASS}
              title={hiddenTitle}
              aria-label={text.more(hidden.length)}
              style={{
                ...CRUMB_TYPE,
                display: 'inline-flex',
                alignItems: 'center',
                height: 22,
                padding: `0 ${spacing.xxs + 3}px`,
                borderRadius: token.borderRadiusSM,
                background: token.colorFillQuaternary,
                color: token.colorTextSecondary,
              }}
            >
              &hellip;
            </span>
          </li>
        )}
        {tail.map((item, index) => crumb(item, VISIBLE_HEAD + 1 + index))}
        {current !== undefined && (
          <li style={LIST_ITEM} aria-current="page">
            {items.length > 0 && <SlashSeparator token={token} />}
            {/* The current level: body ink at regular weight. Colour alone separates it from the
                links before it - it is where you are, not the loudest thing on the line, and the
                title beneath already carries the page's weight. */}
            <span style={{ ...CRUMB_TYPE, color: token.colorText, fontWeight: fontWeight.regular }}>{current}</span>
          </li>
        )}
      </ol>
    </nav>
  )

  return (
    <span className={TRAIL_CLASS} style={{ display: 'contents' }}>
      <style>{trailStyles(token)}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
        {parent && (
          <Link
            to={parent.to}
            className={BACK_CLASS}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              // The same box as a medium Button, which is what a ghost control beside 14px text is.
              width: token.controlHeight,
              height: token.controlHeight,
              // Pulled left by its own inner padding, so the chevron sits on the title's left edge
              // rather than the box doing so.
              marginLeft: -spacing.xs,
              flex: 'none',
              borderRadius: token.borderRadius,
              color: token.colorTextSecondary,
            }}
          >
            <BackChevron />
            {/* An icon-only control needs a name, and the design system mints no copy - so the
                name is the parent's own label, already translated by the caller. */}
            <span style={VISUALLY_HIDDEN}>{parent.label}</span>
          </Link>
        )}
        {trail}
      </div>
      {/* Separates the chrome from the page, so it reaches the same edges the content does. */}
      <HeaderRule />
    </span>
  )
}

/** A slash, at the trail's own muted weight. Chevrons read as "go forward" next to a back control
 * that means the opposite; a slash is punctuation and says nothing. */
function SlashSeparator({ token }: { token: GlobalToken }) {
  return (
    <span aria-hidden style={{ padding: `0 ${spacing.xxs + 2}px`, color: token.colorTextQuaternary }}>
      /
    </span>
  )
}

function BackChevron() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m15 18-6-6 6-6" />
    </svg>
  )
}

export default PageHeaderTrail
